import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { Modules } from 'constants';
import { GoogleAnalytics, WidgetMetric, GA_TRACKER_NAME } from '@conversion-cloud-widget/analytics';
import { Events, publish } from '@conversion-cloud-widget/pubsub';
import { isMobile, LauncherSize } from '@conversion-cloud-widget/UI';
import { PREVIEW } from './constants';
import { loadPublicAppData, get } from './App.helpers';
import { openLiveChat } from './utils/ModuleUtils';

const injectDrawer = hidden => {
    const existingDrawer = document.querySelector('#clx-drawer');
    const searchParams = new URLSearchParams(window.location.search);
    const isProofingMode = searchParams.get('test');
    const showWidget = window[PREVIEW] || !hidden || isProofingMode ? 'block' : 'none';

    if (existingDrawer) {
        existingDrawer.style.display = showWidget;
    } else {
        const clxDrawer = document.querySelector('#clx-drawer') || document.createElement('div');
        clxDrawer.id = 'clx-drawer';
        clxDrawer.style.display = showWidget;

        document.body.appendChild(clxDrawer);
    }
};

const injectSpacer = (size, backgroundColor) => {
    if (document.querySelector('#clx-spacer')) {
        return;
    }
    const clxSpacer = document.createElement('div');
    clxSpacer.id = 'clx-spacer';
    clxSpacer.style.backgroundColor = backgroundColor;
    clxSpacer.style.height = `${size ? LauncherSize[size].height : LauncherSize.large.height}px`;
    clxSpacer.style.width = '100%';

    document.body.appendChild(clxSpacer);
};

const injectCustomCss = (desktop, mobile) => {
    if (!desktop?.length && !mobile?.length) {
        return;
    }

    const styles = isMobile() ? mobile || '' : desktop || '';

    if (!styles.length) {
        return;
    }

    const css = document.createElement('style');
    css.setAttribute('type', 'text/css');

    if (css.styleSheet) {
        css.styleSheet.cssText = styles;
    } else {
        css.appendChild(document.createTextNode(styles));
    }

    document.getElementsByTagName('head')[0].appendChild(css);
};

const loadWidgetConfiguration = async api_key => {
    // The widgetWrapper might have already made this call and stashed the result for us.
    // If so, use it instead of hitting the API again.
    const config = sessionStorage.getItem(process.env.TCC_SESSION_STORAGE_WIDGET_CONFIG);

    if (config) {
        return JSON.parse(config);
    }

    const options = {
        method: 'POST',
        headers: {
            'Content-type': 'Application/json'
        },
        body: JSON.stringify({
            api_key: api_key
        })
    };

    // Call the public_token endpoint to get our widget configuration
    const configurationRequest = await fetch(`${process.env.TCC_API_URL}/public_token`, options);
    if (configurationRequest.status === 404) {
        // Logging for easy debugging
        console.log('Invalid API key');
        return;
    }

    const configurationJson = await configurationRequest.json();

    // Store widget data in session storage
    sessionStorage.setItem(process.env.TCC_SESSION_STORAGE_WIDGET_CONFIG, JSON.stringify(configurationJson));

    return configurationJson;
};

const launch = api_key => {
    /* This is some internal clean up to prevent the widget from being loaded more than once */
    const scripts = document.querySelectorAll('script[src*="ConversionCloudContainer.js"]');
    if (scripts.length > 1) {
        const scriptToKeep = scripts[0];
        [...scripts].map(script => {
            if (script !== scriptToKeep) {
                script.remove();
            }
        });
    }

    /* If the widget is already loaded into the global window, we don't need to do anything else */
    if (window.TCC_LAUNCHED) {
        return;
    }

    // Add preview mode to the window if the preview query param exists to show the widget in preview mode.
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('preview')) {
        window[PREVIEW] = true;
    }

    try {
        // Set up a modules collection to pass to the app
        async function load() {
            const widgetConfiguration = await loadWidgetConfiguration(api_key);
            if (!widgetConfiguration) {
                return;
            }

            const {
                company_id,
                ga_id = '',
                gm_id = '',
                token,
                widget_disabled,
                widget_hidden,
                widget_spacer_color,
                widget_spacer_enabled,
                widget_size,
                widget_code_css,
                widget_code_mobile_css
            } = widgetConfiguration;

            // If the widget is disabled, bounce
            if (widget_disabled) {
                return;
            }

            // Inject the clx drawer element so that we can mount to it
            injectDrawer(widget_hidden);

            // Inject the spacer based on the widget size
            if (widget_spacer_enabled) {
                injectSpacer(widget_size, widget_spacer_color);
            }

            // Inject any custom css
            injectCustomCss(widget_code_css, widget_code_mobile_css);

            const publicApps = await loadPublicAppData(widgetConfiguration);
            // Only enable analytics if the window is not in preview mode
            if (!window[PREVIEW]) {
                // Configure and start widget metric
                const widgetMetric = WidgetMetric(
                    process.env.TCC_API_URL,
                    company_id,
                    publicApps,
                    token,
                    widgetConfiguration
                );
                widgetMetric.startSession();

                // Initialize Google Analytics tracking
                if (ga_id?.length || gm_id?.length) {
                    const ga = GoogleAnalytics(
                        { ga_id, gm_id },
                        {
                            name: GA_TRACKER_NAME,
                            siteSpeedSampleRate: 100,
                            useExistingGa: true
                        }
                    );

                    ga && ga.initialize();
                }
            }

            // Mount the container
            ReactDOM.render(
                <App
                    widgetConfiguration={widgetConfiguration}
                    publicApps={publicApps}
                    reload={widget_spacer_enabled}
                />,
                document.querySelector('#clx-drawer')
            );

            handleQueryString(searchParams, publicApps, token);
        }
        load();
        window.TCC_LAUNCHED = true;
    } catch (e) {
        console.log('Failed to launch widget', {
            error: e
        });
    }
};

const handleQueryString = async (searchParams, publicApps, token, brandPrimaryColor) => {
    if (!window.location.search) {
        return;
    }

    /* The widget can be opened via querystring by passing like this to the URL:
     * site.com/?modalWidget=sg (for Schedule Genie)
     * OR
     * site.com/?modalWidget=sl&offerId=123 (for Concession Manager with a specific offer id - only CM has the additional param for now)
     */
    const qsModule = searchParams.get('modalWidget');
    if (!qsModule) {
        return;
    }

    const qsOffer = searchParams.get('offerId');
    const module = Modules[qsModule.toUpperCase()];

    const retryTimeInMs = 100;
    const maxRetries = 300;

    let retryAttempts = 0;
    // Check to make sure the module is loaded before trying to open it
    if (module === 'MODULE_CHAT') {
        const publicApp = publicApps.find(item => item.name === 'MODULE_CHAT');
        const { result } = await get(`api/chat/${publicApp?.app_data?.chat_id}`, token);
        if (typeof window.showChatWindow !== 'undefined') {
            window.showChatWindow();
        }

        if (result.bd_id) {
            openLiveChat(result.bd_id, brandPrimaryColor);
        }
    }
    const pollForModule = () => {
        setTimeout(() => {
            if (retryAttempts >= maxRetries) {
                console.log(`Could not open module from querystring - ${window.location.search}`, {
                    site: document.location.href
                });
                return;
            }

            const moduleBar = window[`${module}_BAR`];
            if (moduleBar) {
                publish(Events.OPEN_MODULE, {
                    module: module,
                    asModal: true,
                    additionalArgs: qsOffer ? { offerId: qsOffer } : {}
                });
                return;
            } else {
                retryAttempts += 1;
                setTimeout(pollForModule, retryTimeInMs);
            }
        }, retryTimeInMs);
    };
    setTimeout(pollForModule, retryTimeInMs);
};

const openWidget = async (moduleName, asModal = false, additionalArgs = {}) => {
    if (!moduleName) {
        return;
    }

    const module = Modules[moduleName.toUpperCase()];
    if (!module) {
        return;
    }

    if (module === 'MODULE_CHAT') {
        const widgetConfiguration = JSON.parse(sessionStorage.getItem('conversion_cloud_widget_config'));
        if (!widgetConfiguration) {
            return;
        }
        const { token } = widgetConfiguration;
        const publicApps = JSON.parse(sessionStorage.getItem('conversion_cloud_public_apps'));
        const publicApp = publicApps.find(item => item.name === 'MODULE_CHAT');
        const { result } = await get(`api/chat/${publicApp?.app_data?.chat_id}`, token);

        if (result.bd_id) {
            openLiveChat(result.bd_id);
        }
    }

    publish(Events.OPEN_MODULE, {
        module,
        asModal,
        additionalArgs
    });
};

// Add widget to the global scope and expose the launch method
window.TheConversionCloud = {
    launch,
    openWidget
};

// If we are running in development, just launch with the test API key
if (process.env.NODE_ENV === 'development') {
    launch(process.env.WIDGET_API_KEY);
}
